import type { Attributes } from '../stats';
import { type AuraLook, AURAS } from './auras';
import type { Element } from './elements';

export type NeigongCategory = 'jianghu' | 'school';
export const NEIGONG_CATEGORY_LABELS: Readonly<Record<NeigongCategory, string>> = {
  jianghu: 'กำลังภายในยุทธจักร',
  school: 'กำลังภายในสำนัก',
};

export interface NeigongDef {
  readonly id: string;
  readonly name: string;
  readonly desc: string;
  /** Attribute gain per level of this neigong. Only the active neigong applies. */
  readonly perLevel: Partial<Attributes>;
  /** Arts of the same element hit harder while this neigong is active. */
  readonly element: Element;
  /** Scripture tier (ขั้น 1, 2, 3 …); higher tiers come later in the game. */
  readonly tier: number;
  /** Jianghu (ยุทธจักร) neigong are open to anyone; school neigong belong to one school. */
  readonly category: NeigongCategory;
  readonly schoolId?: string;
  readonly maxLevel: number;
  /**
   * The aura a master of this neigong (at max level) carries: one of the reference auras (`ref`,
   * data/auras), its look and colour (rgb), under the neigong's own name for it.
   */
  readonly aura: {
    readonly ref: string;
    readonly style: AuraLook;
    readonly name: string;
    readonly color: string;
  };
  /** Lasting buff (a passive status) granted while active once trained to `level`. */
  readonly passive: { readonly level: number; readonly status: string };
  /**
   * Cultivation to learn it in the neigong panel. Without one it is only ever given (on joining a
   * school, or by a quest).
   */
  readonly learnCost?: number;
  /** A scripture that must first be trained to `level` (a higher tier opens when the lower is mastered). */
  readonly requires?: { readonly neigong: string; readonly level: number };
}

/**
 * Why the player cannot learn this neigong now, or null when they can (cultivation aside).
 * Shared by the World and the panel so the two always agree.
 */
export function neigongLearnBlock(
  levels: Readonly<Record<string, number>>,
  school: string | null,
  id: string,
): string | null {
  const n = NEIGONG[id];
  if (!n || n.learnCost === undefined) return 'ไม่มีทางเรียนด้วยแต้มฝึกปรือ';
  if (levels[id] != null) return 'เรียนแล้ว';
  if (n.schoolId && school !== n.schoolId) return 'ต้องเป็นศิษย์สำนักนี้ก่อน';
  const req = n.requires && NEIGONG[n.requires.neigong];
  if (n.requires && req && (levels[req.id] ?? 0) < n.requires.level)
    return `ต้องฝึก${req.name}ถึงระดับ ${n.requires.level} ก่อน`;
  return null;
}

/** The passive buff the active neigong grants at its level, or null. */
export function neigongPassive(levels: Readonly<Record<string, number>>, id: string): string | null {
  const n = NEIGONG[id];
  return n && (levels[id] ?? 0) >= n.passive.level ? n.passive.status : null;
}

/** A neigong's mastery aura: reference aura `ref` (data/auras) under the neigong's own name. */
function aura(ref: string, name: string): NeigongDef['aura'] {
  const a = AURAS[ref];
  if (!a) throw new Error(`Unknown aura: ${ref}`);
  return { ref, style: a.look, name, color: a.color };
}

/** The neigong looks the 2D client first drew (data/auras `AuraLook` holds them all). */
export type AuraStyle = 'flame' | 'mist' | 'taiji' | 'bell' | 'blades' | 'petals' | 'ink' | 'bloodstorm';

/** True when the active neigong has been trained to its peak (and so shows its aura). */
export function neigongMastered(levels: Readonly<Record<string, number>>, id: string): boolean {
  const n = NEIGONG[id];
  return !!n && (levels[id] ?? 0) >= n.maxLevel;
}

const list: NeigongDef[] = [
  {
    id: 'blood-oath',
    name: 'คัมภีร์สัญญาเลือด',
    desc: 'กำลังภายในที่ผูกกับสัญญาเลือด เน้นพลังโจมตีและความว่องไว',
    perLevel: { str: 2, dex: 1 },
    element: 'yang',
    tier: 1,
    category: 'jianghu',
    maxLevel: 20,
    passive: { level: 6, status: 'np-blood' },
    aura: aura('blazing-fire', 'เพลิงโลหิต'),
  },
  {
    id: 'cloud-heart',
    name: 'จิตเมฆาไร้รูป',
    desc: 'ลมปราณไหลเวียนดุจเมฆ เพิ่มลมปราณสูงสุดและการฟื้นฟู เหมาะกับการใช้กระบวนท่าต่อเนื่อง',
    perLevel: { spi: 2, dex: 1 },
    element: 'soft',
    tier: 1,
    category: 'jianghu',
    maxLevel: 20,
    passive: { level: 6, status: 'np-cloud' },
    aura: aura('flowing-water', 'เมฆาห่อกาย'),
  },
  {
    id: 'mist-breath',
    name: 'ลมปราณยอดหมอก',
    desc: 'กำลังภายในประจำสำนักยอดหมอก อ่านทางลมในหมอก เพิ่มพลังชีวิตและพลังป้องกัน ก้าวเลี่ยงแรงได้ดี',
    perLevel: { sta: 2, ing: 1 },
    element: 'taiji',
    tier: 1,
    category: 'school',
    schoolId: 'mist-peak',
    maxLevel: 20,
    passive: { level: 6, status: 'np-mist-breath' },
    aura: aura('jade', 'หยินหยางหมุนเวียน'),
  },
  {
    id: 'bell-body',
    name: 'คัมภีร์หลอมกายระฆัง',
    desc: 'กำลังภายในประจำสำนักหินระฆัง หล่อหลอมกายดุจเนื้อระฆังทองแดง พลังชีวิตหนาแน่น ทนทานต่อทุกแรงกระแทก',
    perLevel: { sta: 2, str: 1 },
    element: 'hard',
    tier: 1,
    category: 'school',
    schoolId: 'bell-stone',
    maxLevel: 20,
    passive: { level: 6, status: 'np-vajra' },
    aura: aura('earth', 'ระฆังทองพุทธะ'),
  },
  {
    id: 'road-iron',
    name: 'กายาเหล็กทางโคม',
    desc: 'กำลังภายในของทางโคมเหล็ก ฝึกให้กล้ามเนื้อแกร่งและมือไวดุจสายฟ้า เพื่อรักษากติกาบนทางโคม',
    perLevel: { str: 2, dex: 1 },
    element: 'yang',
    tier: 1,
    category: 'school',
    schoolId: 'lantern-road',
    maxLevel: 20,
    passive: { level: 6, status: 'np-guard' },
    aura: aura('gold', 'ประกายดาบพิทักษ์'),
  },
  {
    id: 'mourning-heart',
    name: 'จิตไว้อาลัยจันทร์มัว',
    desc: 'กำลังภายในแห่งหุบเขาจันทร์มัว ลมหายใจนิ่งเนิบดุจเสียงไว้อาลัยยามค่ำ หล่อเลี้ยงพลังโจมตีภายในอันลึกลับ',
    perLevel: { ing: 2, spi: 1 },
    element: 'yin',
    tier: 1,
    category: 'school',
    schoolId: 'dim-moon',
    maxLevel: 20,
    passive: { level: 6, status: 'np-bliss' },
    aura: aura('falling-blossoms', 'กลีบมารจันทร์มัว'),
  },
  {
    id: 'ink-breath',
    name: 'ลมปราณหมึกสน',
    desc: 'กำลังภายในของหอหมึกสน จิตนิ่งดุจหมึกบนกระดาษ ลมปราณใสสะอาด แม่นยำและไหลเวียนไม่ขาดสาย',
    perLevel: { spi: 2, ing: 1 },
    element: 'soft',
    tier: 1,
    category: 'school',
    schoolId: 'pine-ink',
    maxLevel: 20,
    passive: { level: 6, status: 'np-scholar' },
    aura: aura('violet-mystery', 'หมึกสน'),
  },
  {
    id: 'ember-heart',
    name: 'คัมภีร์คมชาด',
    desc: 'กำลังภายในของสำนักดาบชาด ปลุกไฟในกายให้ร้อนแรง ยิ่งฟันยิ่งแกร่ง ดื่มไฟศัตรูมาหล่อเลี้ยงตน',
    perLevel: { str: 2, sta: 1 },
    element: 'yang',
    tier: 1,
    category: 'school',
    schoolId: 'cinnabar',
    maxLevel: 20,
    passive: { level: 6, status: 'np-bloodthirst' },
    aura: aura('blood-blossom', 'พายุโลหิตอัสนี'),
  },
  // ── ขั้น 2: เปิดเมื่อฝึกคัมภีร์ขั้น 1 สายเดียวกันจนตัน ──
  {
    id: 'nine-heart',
    name: 'จิตกระบี่เก้าวิญญาณ',
    desc: 'คัมภีร์ขั้นสองต่อจากสัญญาเลือด ผู้ที่ฝึกสัญญาเลือดจนตันจะได้ยินเสียงกระบี่ทั้งเก้าในใจ พลังโจมตี ความว่องไว และพลังจิตเพิ่มพร้อมกัน',
    perLevel: { str: 2, dex: 1, spi: 1 },
    element: 'yang',
    tier: 2,
    category: 'jianghu',
    maxLevel: 20,
    learnCost: 800,
    requires: { neigong: 'blood-oath', level: 20 },
    passive: { level: 6, status: 'np-nine' },
    aura: aura('gold', 'เก้ากระบี่วนกาย'),
  },
  {
    id: 'cloud-void',
    name: 'จิตเมฆาว่างเปล่า',
    desc: 'คัมภีร์ขั้นสองต่อจากจิตเมฆาไร้รูป เมื่อเมฆไร้รูปก็ไร้สิ่งใดให้ทำร้าย ลมปราณลึกยาว กายเบาดุจไร้น้ำหนัก',
    perLevel: { spi: 2, dex: 1, ing: 1 },
    element: 'soft',
    tier: 2,
    category: 'jianghu',
    maxLevel: 20,
    learnCost: 800,
    requires: { neigong: 'cloud-heart', level: 20 },
    passive: { level: 6, status: 'np-void' },
    aura: aura('mist-cloud', 'เมฆาเหนือฟ้า'),
  },
  {
    id: 'mist-wheel',
    name: 'หมอกวนแปดทิศ',
    desc: 'คัมภีร์ขั้นสองของยอดหมอก ลมหมุนเวียนครบแปดทิศ รับแรงจากทุกด้านแล้วสลายไป พลังชีวิตและลมปราณหนาแน่น',
    perLevel: { sta: 2, ing: 1, spi: 1 },
    element: 'taiji',
    tier: 2,
    category: 'school',
    schoolId: 'mist-peak',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'mist-breath', level: 20 },
    passive: { level: 6, status: 'np-mist-wheel' },
    aura: aura('jade', 'แปดทิศหยินหยาง'),
  },
  {
    id: 'bell-layers',
    name: 'คัมภีร์ระฆังพันชั้น',
    desc: 'คัมภีร์ขั้นสองของหินระฆัง คู่กับคัมภีร์หลอมกายระฆัง หลอมกายซ้ำพันชั้นจนแน่นดุจเนื้อระฆัง ทนทานยิ่งกว่าระฆังทอง',
    perLevel: { sta: 2, str: 1, spi: 1 },
    element: 'hard',
    tier: 2,
    category: 'school',
    schoolId: 'bell-stone',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'bell-body', level: 20 },
    passive: { level: 6, status: 'np-bell-layers' },
    aura: aura('earth', 'ระฆังพันพุทธะ'),
  },
  {
    id: 'lamp-spark',
    name: 'กายาประกายโคม',
    desc: 'คัมภีร์ขั้นสองของทางโคมเหล็ก สอนเฉพาะผู้ถือตะเกียงเหล็กประจำทาง กล้ามเนื้อแกร่งและว่องไวดุจประกายโคม',
    perLevel: { str: 2, dex: 2 },
    element: 'yang',
    tier: 2,
    category: 'school',
    schoolId: 'lantern-road',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'road-iron', level: 20 },
    passive: { level: 6, status: 'np-fish' },
    aura: aura('gold', 'ปลาบินพิทักษ์บัลลังก์'),
  },
  {
    id: 'vigil-heart',
    name: 'จิตเงาจันทร์',
    desc: 'คัมภีร์ขั้นสองแห่งหุบเขาจันทร์มัว จิตที่เฝ้าคนตายมาทั้งคืน พลังโจมตีภายในลึกล้ำ ร่างเบา และฟื้นพลังคืนทุกครั้งที่โดน',
    perLevel: { ing: 2, spi: 1, dex: 1 },
    element: 'yin',
    tier: 2,
    category: 'school',
    schoolId: 'dim-moon',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'mourning-heart', level: 20 },
    passive: { level: 6, status: 'np-demon' },
    aura: aura('falling-blossoms', 'กลีบเงาจันทร์'),
  },
  {
    id: 'ink-stillness',
    name: 'ลมปราณหมึกนิ่ง',
    desc: 'คัมภีร์ขั้นสองของหอหมึกสน จิตใสจนเห็นทุกจุดอ่อนของศัตรู พลังจิตและพลังโจมตีภายในเพิ่มพร้อมกัน',
    perLevel: { spi: 2, ing: 2 },
    element: 'soft',
    tier: 2,
    category: 'school',
    schoolId: 'pine-ink',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'ink-breath', level: 20 },
    passive: { level: 6, status: 'np-sage' },
    aura: aura('violet-mystery', 'หมึกปราชญ์'),
  },
  {
    id: 'ember-sea',
    name: 'คัมภีร์ทะเลถ่านแดง',
    desc: 'คัมภีร์ขั้นสองของสำนักดาบชาด ไฟที่ดื่มมาทั้งชีวิตกลายเป็นทะเลถ่านแดง ยิ่งฟันยิ่งอิ่ม กล้ามเนื้อและความอึดแกร่งดุจผาแดง',
    perLevel: { str: 2, sta: 2 },
    element: 'yang',
    tier: 2,
    category: 'school',
    schoolId: 'cinnabar',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'ember-heart', level: 20 },
    passive: { level: 6, status: 'np-bloodsea' },
    aura: aura('blood-blossom', 'ทะเลโลหิต'),
  },
  // ── H18: กำลังภายในสำนัก 3 ขั้นต่อสำนัก (ขั้น 1 ให้ตอนเข้าสำนัก; ขั้น 2/3 ปลดตามกติกาใน systems/sectNeigong.ts) ──
  {
    id: 'tide-rhythm',
    name: 'คลื่นสามจังหวะ',
    desc: 'กำลังภายในประจำสำนักคลื่นไผ่ ซัดเข้า ถอย แตกฝั่ง ลมหายใจสามจังหวะที่ทำให้ก้าวไม่เคยพลาดจังหวะ ลมปราณลื่นไหลต่อเนื่อง',
    perLevel: { spi: 2, dex: 1 },
    element: 'soft',
    tier: 1,
    category: 'school',
    schoolId: 'bamboo-tide',
    maxLevel: 20,
    passive: { level: 6, status: 'np-tide' },
    aura: aura('flowing-water', 'คลื่นไผ่ห่อกาย'),
  },
  {
    id: 'tide-swell',
    name: 'คลื่นไผ่หนุนส่ง',
    desc: 'คัมภีร์ขั้นสองของสำนักคลื่นไผ่ คลื่นลูกใหม่ซ้อนทับลูกเก่า ลมปราณลึกขึ้นและก้าวเบาขึ้นทุกจังหวะ',
    perLevel: { spi: 2, dex: 1, sta: 1 },
    element: 'soft',
    tier: 2,
    category: 'school',
    schoolId: 'bamboo-tide',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'tide-rhythm', level: 20 },
    passive: { level: 6, status: 'np-tide2' },
    aura: aura('mist-cloud', 'คลื่นหนุนไผ่'),
  },
  {
    id: 'snow-hush',
    name: 'ปราณหิมะสงัด',
    desc: 'กำลังภายในประจำตำหนักสนหิมะ ลมหายใจเย็นนิ่งดุจหิมะแรกของฤดู เก็บแรงไว้ในกายและปล่อยเมื่อศัตรูพลาด',
    perLevel: { ing: 2, sta: 1 },
    element: 'yin',
    tier: 1,
    category: 'school',
    schoolId: 'snow-pine',
    maxLevel: 20,
    passive: { level: 6, status: 'np-snow' },
    aura: aura('mist-cloud', 'น้ำค้างแข็งห่อกาย'),
  },
  {
    id: 'pine-stand',
    name: 'สนหิมะยืนต้น',
    desc: 'คัมภีร์ขั้นสองของตำหนักสนหิมะ ยืนนิ่งใต้หิมะจนรากหยั่งลึก พลังโจมตีภายในและความอึดเพิ่มพร้อมกัน',
    perLevel: { ing: 2, sta: 1, spi: 1 },
    element: 'yin',
    tier: 2,
    category: 'school',
    schoolId: 'snow-pine',
    maxLevel: 20,
    learnCost: 1000,
    requires: { neigong: 'snow-hush', level: 20 },
    passive: { level: 6, status: 'np-snow2' },
    aura: aura('mist-cloud', 'สนยืนต้นกลางหิมะ'),
  },
  // ขั้น 3 ของทุกสำนัก (ปลดโดยสำนักเท่านั้น ไม่มีทางซื้อ)
  {
    id: 'mist-crown',
    name: 'ม่านหมอกเหนือยอด',
    desc: 'คัมภีร์ขั้นสามของยอดหมอก หมอกที่คลุมเหนือยอดเขาไม่เคยหนีไปไหน รับแรงทุกด้านแล้วคืนกลับด้วยความนิ่ง',
    perLevel: { sta: 2, ing: 2, spi: 1 },
    element: 'taiji',
    tier: 3,
    category: 'school',
    schoolId: 'mist-peak',
    maxLevel: 20,
    requires: { neigong: 'mist-wheel', level: 15 },
    passive: { level: 6, status: 'np-mist3' },
    aura: aura('jade', 'ม่านหมอกเหนือยอด'),
  },
  {
    id: 'crimson-sky',
    name: 'ชาดสิ้นฟ้า',
    desc: 'คัมภีร์ขั้นสามของสำนักดาบชาด เลือดเดือดจนย้อมฟ้าเป็นสีชาด แรงฟันและความอึดแกร่งสุดขีด',
    perLevel: { str: 2, sta: 2, dex: 1 },
    element: 'yang',
    tier: 3,
    category: 'school',
    schoolId: 'cinnabar',
    maxLevel: 20,
    requires: { neigong: 'ember-sea', level: 15 },
    passive: { level: 6, status: 'np-crimson3' },
    aura: aura('blood-blossom', 'ฟ้าชาดสิ้นแสง'),
  },
  {
    id: 'ink-boundless',
    name: 'หมึกสนไร้ขอบ',
    desc: 'คัมภีร์ขั้นสามของหอหมึกสน หมึกหนึ่งหยดไหลได้ไม่รู้จบ จิตใสจนทุกจุดอ่อนปรากฏเป็นเส้น',
    perLevel: { spi: 2, ing: 2, dex: 1 },
    element: 'soft',
    tier: 3,
    category: 'school',
    schoolId: 'pine-ink',
    maxLevel: 20,
    requires: { neigong: 'ink-stillness', level: 15 },
    passive: { level: 6, status: 'np-ink3' },
    aura: aura('violet-mystery', 'หมึกไร้ขอบกระดาษ'),
  },
  {
    id: 'bell-silent',
    name: 'ระฆังไร้เสียง',
    desc: 'คัมภีร์ขั้นสามของหินระฆัง ระฆังที่หนักจนไม่ต้องดัง กายแน่นดุจหินและทนทานเหนือทุกแรงกระแทก',
    perLevel: { sta: 2, str: 2, spi: 1 },
    element: 'hard',
    tier: 3,
    category: 'school',
    schoolId: 'bell-stone',
    maxLevel: 20,
    requires: { neigong: 'bell-layers', level: 15 },
    passive: { level: 6, status: 'np-bell3' },
    aura: aura('earth', 'ระฆังเงียบก้อง'),
  },
  {
    id: 'lantern-night',
    name: 'โคมเหล็กส่องคืน',
    desc: 'คัมภีร์ขั้นสามของทางโคมเหล็ก แสงโคมเหล็กที่ไม่เคยดับกลางคืน แกร่ง ไว และเฝ้าระวังทุกทิศ',
    perLevel: { str: 2, dex: 2, sta: 1 },
    element: 'yang',
    tier: 3,
    category: 'school',
    schoolId: 'lantern-road',
    maxLevel: 20,
    requires: { neigong: 'lamp-spark', level: 15 },
    passive: { level: 6, status: 'np-lantern3' },
    aura: aura('gold', 'โคมเหล็กส่องคืน'),
  },
  {
    id: 'moon-veil',
    name: 'ม่านจันทร์ลวง',
    desc: 'คัมภีร์ขั้นสามของหุบจันทร์มัว ม่านจันทร์ที่บังให้เห็นเพียงเงา พลังภายในลึกล้ำและร่างเบาดุจไร้ตัวตน',
    perLevel: { ing: 2, spi: 1, dex: 2 },
    element: 'yin',
    tier: 3,
    category: 'school',
    schoolId: 'dim-moon',
    maxLevel: 20,
    requires: { neigong: 'vigil-heart', level: 15 },
    passive: { level: 6, status: 'np-moonveil3' },
    aura: aura('falling-blossoms', 'กลีบจันทร์ลวงตา'),
  },
  {
    id: 'tide-root',
    name: 'คลื่นกลับรากไผ่',
    desc: 'คัมภีร์ขั้นสามของสำนักคลื่นไผ่ คลื่นที่ซัดออกไปไกลสุดย่อมกลับมาที่ราก ลมปราณลื่นไหลและก้าวไวไร้ที่ติ',
    perLevel: { spi: 2, dex: 2, sta: 1 },
    element: 'soft',
    tier: 3,
    category: 'school',
    schoolId: 'bamboo-tide',
    maxLevel: 20,
    requires: { neigong: 'tide-swell', level: 15 },
    passive: { level: 6, status: 'np-tide3' },
    aura: aura('mist-cloud', 'รากไผ่ใต้คลื่น'),
  },
  {
    id: 'frost-root',
    name: 'ธารน้ำแข็งใต้ราก',
    desc: 'คัมภีร์ขั้นสามของตำหนักสนหิมะ ธารเยือกใต้รากสนไหลช้าแต่ไม่เคยหยุด พลังโจมตีภายในและความอึดหนาแน่นดุจน้ำแข็งพันปี',
    perLevel: { ing: 2, sta: 2, spi: 1 },
    element: 'yin',
    tier: 3,
    category: 'school',
    schoolId: 'snow-pine',
    maxLevel: 20,
    requires: { neigong: 'pine-stand', level: 15 },
    passive: { level: 6, status: 'np-snow3' },
    aura: aura('mist-cloud', 'ธารเยือกใต้ราก'),
  },
];

export const NEIGONG: Readonly<Record<string, NeigongDef>> = Object.fromEntries(list.map((n) => [n.id, n]));

export function getNeigong(id: string): NeigongDef {
  const n = NEIGONG[id];
  if (!n) throw new Error(`Unknown neigong: ${id}`);
  return n;
}

/**
 * H18: the three sect neigong of each of the 8 sects, tier 1 → 3 (tier 1 is given on joining; tiers 2 and 3 open in order,
 * see systems/sectNeigong.ts). The unlock numbers are tunable: tier 2 needs tier 1 at `T2_GATE.level` and rank
 * ศิษย์เอก (badge tier 2); tier 3 needs tier 2 at `T3_GATE.level` and rank ผู้อาวุโส (badge tier 3).
 */
export const SECT_NEIGONG: Readonly<Record<string, readonly [string, string, string]>> = {
  'bamboo-tide': ['tide-rhythm', 'tide-swell', 'tide-root'],
  'mist-peak': ['mist-breath', 'mist-wheel', 'mist-crown'],
  'cinnabar': ['ember-heart', 'ember-sea', 'crimson-sky'],
  'pine-ink': ['ink-breath', 'ink-stillness', 'ink-boundless'],
  'bell-stone': ['bell-body', 'bell-layers', 'bell-silent'],
  'lantern-road': ['road-iron', 'lamp-spark', 'lantern-night'],
  'dim-moon': ['mourning-heart', 'vigil-heart', 'moon-veil'],
  'snow-pine': ['snow-hush', 'pine-stand', 'frost-root'],
};
export const SECT_UNLOCK = {
  2: { level: 10, badge: 2 },
  3: { level: 15, badge: 3 },
} as const;
